import { z } from 'zod';
/**
 * The scalar vocabulary. Every one of these exists because the provider
 * agreement (docs/provider-api-conformance.md, section 2) makes a promise
 * about it that a bare `string` or `number` would not keep.
 */
/**
 * ISO-8601 instant with an **explicit** UTC offset — `2026-09-03T13:00:00.000Z`
 * or `2026-09-03T09:00:00.000-04:00`. A local wall-clock string without an
 * offset is rejected: the agreement (C3) is specific that it never appears on
 * the wire, and a naive datetime is the classic source of a booking an hour off.
 */
export const IsoDateTime = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/, 'must be ISO-8601 with an explicit UTC offset')
    .refine((s) => !Number.isNaN(Date.parse(s)), 'not a real instant');
/** A calendar date in the tenant's timezone — `2026-09-03` (agreement C4). */
export const IsoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD')
    .refine((s) => {
    const [y, m, d] = s.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}, 'not a real calendar date');
/** Wall-clock time of day in the tenant's timezone — `08:00`, `18:30`. */
export const TimeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'must be HH:MM');
/**
 * Money is **integer cents** and nothing else (agreement C5). Not a float, not
 * a string, not a Decimal. `12500` with `"USD"` means $125.00.
 */
export const Cents = z.number().int().min(-1_000_000_000).max(1_000_000_000);
/** A non-negative amount — prices, values, fees. */
export const PositiveCents = z.number().int().min(0).max(1_000_000_000);
export const CurrencyCode = z.string().regex(/^[A-Z]{3}$/, 'must be an ISO-4217 code');
/** An IANA timezone name, validated against the runtime's own database. */
export const Timezone = z.string().refine((tz) => {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
}, 'not a known IANA timezone');
/** Monday-first, matching LoveSpa's `WeekSchedule` so the importer is a copy. */
export const Weekday = z.number().int().min(0).max(6);
export const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
/**
 * An identity a guest reached us by. Phones are E.164, emails are lowercased —
 * normalization happens on write so lookup is an index hit (agreement G3).
 *
 * `wa` is v1.5 §2's addition: the E.164 behind a WhatsApp account. It is a
 * **wire** vocabulary, not a storage one — `normalizeIdentity` canonicalizes it
 * to `phone`, which is the "dedupe against `phone` at the provider's
 * discretion" the change doc offers, taken in full: a WhatsApp number IS a
 * phone number, the book stores the number, and a guest who booked by phone is
 * found by their WhatsApp identity (and vice versa) because the two normalize
 * to the same row. `client_identities`' CHECK constraint therefore never
 * changes, and no surface of ours needs to learn a third kind.
 *
 * `site_session` deliberately does NOT join: v1.5 §2.2 records it as a
 * consumer continuity token, meaningless in a client book — a boundary, not an
 * omission.
 */
export const IdentityKind = z.enum(['phone', 'email', 'wa']);
export const Identity = z.object({
    identity_kind: IdentityKind,
    identity: z.string().min(1).max(320),
});
/**
 * Normalize an identity for storage and comparison.
 *
 * Phones: strip everything but digits and a leading `+`, then require E.164.
 * A bare 10-digit US number is given `+1` because that is what a spa's iPad
 * has been storing for years and the importer must not drop those clients.
 *
 * `wa` normalizes exactly as `phone` does and comes back AS `phone` — the
 * canonicalization the `IdentityKind` comment argues. Every write and every
 * lookup passes through here first, so the collapse is total by construction.
 */
export function normalizeIdentity(id, defaultCountry = '1') {
    if (id.identity_kind === 'email') {
        const email = id.identity.trim().toLowerCase();
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
            ? { identity_kind: 'email', identity: email }
            : null;
    }
    const raw = id.identity.trim();
    const plus = raw.startsWith('+');
    const digits = raw.replace(/\D/g, '');
    if (digits.length === 0)
        return null;
    const e164 = plus
        ? `+${digits}`
        : digits.length === 10
            ? `+${defaultCountry}${digits}`
            : `+${digits}`;
    return /^\+[1-9]\d{6,14}$/.test(e164) ? { identity_kind: 'phone', identity: e164 } : null;
}
export function sameIdentity(a, b) {
    const na = normalizeIdentity(a);
    const nb = normalizeIdentity(b);
    return (na !== null &&
        nb !== null &&
        na.identity_kind === nb.identity_kind &&
        na.identity === nb.identity);
}
//# sourceMappingURL=primitives.js.map